import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import Select from "react-select";
import Loader from "../component/Loader";
import logoSvg from "../Assets/images/logo.svg";
import styles from "../Assets/css/weatherdashboard.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
);

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

const WEATHER_DASHBOARD_API = process.env.REACT_APP_BASE_URL + "weather-dashboard/";

const MARKER_COLORS = {
  enphase: "#28a745",
  solArk: "#e0a800",
  hoymiles: "#17a2b8",
};

// =================== Device Chart Sub-Component ===================
const DeviceChart = ({ device }) => {
  const hasWeather = device.historical_weather && device.historical_weather.length > 0;
  const hasProduction = device.historical_production && device.historical_production.length > 0;
  const hasIrradiance = device.historical_irradiance && device.historical_irradiance.length > 0;

  if (!hasWeather && !hasProduction && !hasIrradiance) {
    return (
      <div className={styles.chartEmpty}>
        <span className={styles.chartEmptyIcon}>📊</span>
        <span>No historical data</span>
      </div>
    );
  }

  let labels;
  if (hasWeather) {
    labels = device.historical_weather.map((d) => d.datetime || d.time || "");
  } else if (hasIrradiance) {
    labels = device.historical_irradiance.map((d) => d.datetime || d.time || "");
  } else {
    labels = device.historical_production.map((d) => d.datetime || d.time || "");
  }

  const datasets = [];

  if (hasWeather) {
    datasets.push(
      {
        label: "Temp (°F)",
        data: device.historical_weather.map((d) => d.temperature),
        borderColor: "#ff6b6b",
        backgroundColor: "rgba(255, 107, 107, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 2,
        pointHoverRadius: 4,
        yAxisID: "y",
        type: "line",
      },
      {
        label: "Wind (mph)",
        data: device.historical_weather.map((d) => d.wind_speed),
        borderColor: "#4dabf7",
        backgroundColor: "rgba(77, 171, 247, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 2,
        pointHoverRadius: 4,
        yAxisID: "y",
        type: "line",
      },
      {
        label: "Humidity (%)",
        data: device.historical_weather.map((d) => d.humidity),
        borderColor: "#69db7c",
        backgroundColor: "rgba(105, 219, 124, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 2,
        pointHoverRadius: 4,
        yAxisID: "y1",
        type: "line",
      }
    );
  }

  if (hasProduction) {
    datasets.push({
      label: "Production (kWh)",
      data: device.historical_production.map((d) => d.production),
      backgroundColor: "rgba(76, 175, 80, 0.6)",
      borderColor: "#4caf50",
      borderWidth: 1,
      yAxisID: hasWeather ? "y2" : "y",
      type: "bar",
      barThickness: 8,
    });
  }

  if (hasIrradiance) {
    datasets.push({
      label: "Solar Irradiance (W/m²)",
      data: device.historical_irradiance.map((d) => d.solar_irradiance),
      borderColor: "#ff9800",
      backgroundColor: "rgba(255, 152, 0, 0.15)",
      borderWidth: 2,
      tension: 0.4,
      fill: true,
      pointRadius: 2,
      pointHoverRadius: 4,
      yAxisID: hasWeather ? "y1" : "y",
      type: "line",
    });
  }

  const scales = {
    x: {
      display: true,
      ticks: { font: { size: 8 }, maxRotation: 45, minRotation: 0 },
      grid: { display: false },
    },
  };

  if (hasWeather) {
    scales.y = {
      type: "linear",
      display: true,
      position: "left",
      ticks: { font: { size: 8 } },
      grid: { color: "rgba(0,0,0,0.05)" },
    };
    scales.y1 = {
      type: "linear",
      display: true,
      position: "right",
      ticks: { font: { size: 8 } },
      grid: { drawOnChartArea: false },
    };
    if (hasProduction) {
      scales.y2 = {
        type: "linear",
        display: true,
        position: "right",
        title: { display: true, text: "kWh", font: { size: 8 } },
        ticks: { font: { size: 7 } },
        grid: { drawOnChartArea: false },
      };
    }
  } else {
    scales.y = {
      type: "linear",
      display: true,
      position: "left",
      title: {
        display: true,
        text: hasProduction ? "Production (kWh)" : "W/m²",
        font: { size: 9 },
      },
      ticks: { font: { size: 8 } },
      grid: { color: "rgba(0,0,0,0.05)" },
    };
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { boxWidth: 8, padding: 4, font: { size: 8 } },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.8)",
        titleFont: { size: 10 },
        bodyFont: { size: 9 },
        padding: 6,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              if (context.dataset.label === "Production (kWh)") {
                label += context.parsed.y.toFixed(3) + " kWh";
              } else if (context.dataset.label === "Solar Irradiance (W/m²)") {
                label += context.parsed.y.toFixed(1) + " W/m²";
              } else {
                label += context.parsed.y.toFixed(1);
                if (context.dataset.label === "Temp (°F)") label += "°F";
                else if (context.dataset.label === "Wind (mph)") label += " mph";
                else if (context.dataset.label === "Humidity (%)") label += "%";
              }
            }
            return label;
          },
        },
      },
    },
    scales,
  };

  return (
    <div className={styles.chartContainer}>
      <Line data={{ labels, datasets }} options={options} />
    </div>
  );
};

// =================== Status Badge Sub-Component ===================
const StatusBadge = ({ state }) => {
  if (!state) return null;
  const map = {
    exporting: { cls: styles.statusExporting, icon: "↑", text: "Exporting" },
    importing: { cls: styles.statusImporting, icon: "↓", text: "Importing" },
    idle: { cls: styles.statusIdle, icon: "⏸", text: "Idle" },
    error: { cls: styles.statusError, icon: "⚠", text: "Error" },
  };
  const badge = map[state] || { cls: styles.statusUnknown, icon: "?", text: state };
  return (
    <span className={`${styles.statusBadge} ${badge.cls}`}>
      {badge.icon} {badge.text}
    </span>
  );
};

// =================== Location Weather Card ===================
const LocationWeatherCard = ({ loc }) => (
  <div className={styles.locationCard}>
    <div className={styles.locZipcode}>{loc.zipcode || "--"}</div>
    <div className={styles.locState}>{loc.state || "--"}</div>
    <div className={styles.locWeather}>
      {loc.weather_icon && (
        <img
          src={loc.weather_icon.startsWith("http") ? loc.weather_icon : `https:${loc.weather_icon}`}
          alt=""
          className={styles.locWeatherIcon}
        />
      )}
      <span className={styles.locTemp}>
        {loc.temperature != null ? `${Math.round(loc.temperature)}°F` : "--"}
      </span>
      <span className={styles.locCondition}>{loc.weather_condition || "--"}</span>
    </div>
    <div className={styles.locDetails}>
      <span title="Feels like">🌡 {loc.feelslike != null ? `${Math.round(loc.feelslike)}°F` : "--"}</span>
      <span title="Humidity">💧 {loc.humidity != null ? `${loc.humidity}%` : "--"}</span>
      <span title="Wind">💨 {loc.wind_mph != null || loc.wind_speed != null ? `${Math.round(loc.wind_mph || loc.wind_speed)} mph` : "--"}</span>
      <span title="Cloud">☁ {loc.cloud != null ? `${loc.cloud}%` : "--"}</span>
    </div>
    <div className={styles.locSun}>
      <span>🌅 {loc.sunrise || "--"}</span>
      <span>🌙 {loc.sunset || "--"}</span>
    </div>
    <div className={styles.locDevices}>
      <span className={styles.locDevicesBadge}>{loc.device_count || 0} devices</span>
    </div>
  </div>
);

// =================== Peer Status Badge Sub-Component ===================
const PeerStatusBadge = ({ status }) => {
  const map = {
    green: { cls: styles.peerBadgeGreen, icon: "✓", text: "Normal" },
    yellow: { cls: styles.peerBadgeYellow, icon: "⚠", text: "Warning" },
    red: { cls: styles.peerBadgeRed, icon: "✕", text: "Underperforming" },
  };
  const badge = map[status] || { cls: styles.peerBadgeUnknown, icon: "?", text: "N/A" };
  return (
    <span className={`${styles.peerBadge} ${badge.cls}`}>
      {badge.icon} {badge.text}
    </span>
  );
};

// =================== Peer Performance Summary Sub-Component ===================
const PeerPerformanceSummary = ({ peerPerformance, totalCount, selectedPeerDays, onChangePeerDays }) => {
  const green = peerPerformance?.green_count || 0;
  const yellow = peerPerformance?.yellow_count || 0;
  const red = peerPerformance?.red_count || 0;
  const unknown = peerPerformance?.unknown_count || 0;
  const total = totalCount || (green + yellow + red + unknown) || 1;

  return (
    <div className={styles.peerCard}>
      <div className={styles.peerCardHeader}>
        <span className={styles.peerCardTitle}>
          📊 Solar Peer Performance ({selectedPeerDays}-Day Rolling Window)
        </span>
        <div className={styles.peerWindowBtns}>
          <span className={styles.peerWindowLabel}>Window:</span>
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              className={`${styles.peerWindowBtn} ${selectedPeerDays === d ? styles.peerWindowBtnActive : ""}`}
              onClick={() => onChangePeerDays(d)}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>
      <div className={styles.peerCardBody}>
        <div className={styles.peerStatsRow}>
          <div className={`${styles.peerStatBox} ${styles.peerStatGreen}`}>
            <div className={styles.peerStatNumber}>{green}</div>
            <div className={styles.peerStatLabel}>✓ Normal (≥ -5%)</div>
          </div>
          <div className={`${styles.peerStatBox} ${styles.peerStatYellow}`}>
            <div className={styles.peerStatNumber}>{yellow}</div>
            <div className={styles.peerStatLabel}>⚠ Warning (-15% to -5%)</div>
          </div>
          <div className={`${styles.peerStatBox} ${styles.peerStatRed}`}>
            <div className={styles.peerStatNumber}>{red}</div>
            <div className={styles.peerStatLabel}>✕ Underperforming (≤ -15%)</div>
          </div>
          <div className={`${styles.peerStatBox} ${styles.peerStatUnknown}`}>
            <div className={styles.peerStatNumber}>{unknown}</div>
            <div className={styles.peerStatLabel}>? No Data</div>
          </div>
        </div>
        <div className={styles.peerBarContainer}>
          <div
            className={`${styles.peerBarSegment} ${styles.peerBarGreen}`}
            style={{ width: `${(green / total) * 100}%` }}
            title={`Green: ${green}`}
          />
          <div
            className={`${styles.peerBarSegment} ${styles.peerBarYellow}`}
            style={{ width: `${(yellow / total) * 100}%` }}
            title={`Yellow: ${yellow}`}
          />
          <div
            className={`${styles.peerBarSegment} ${styles.peerBarRed}`}
            style={{ width: `${(red / total) * 100}%` }}
            title={`Red: ${red}`}
          />
          <div
            className={`${styles.peerBarSegment} ${styles.peerBarUnknownSeg}`}
            style={{ width: `${(unknown / total) * 100}%` }}
            title={`Unknown: ${unknown}`}
          />
        </div>
      </div>
    </div>
  );
};

// =================== Device Tile Sub-Component ===================
const DeviceTile = ({ device }) => {
  const headerClass =
    device.device_type === "enphase"
      ? styles.tileHeaderEnphase
      : device.device_type === "solArk"
      ? styles.tileHeaderSolark
      : styles.tileHeaderHoymiles;

  const deviceName = device.name
    ? device.name.length > 24
      ? device.name.substring(0, 24) + "…"
      : device.name
    : `Device #${device.device_id}`;

  const typeLabel =
    device.device_type === "enphase"
      ? "Enphase"
      : device.device_type === "solArk"
      ? "SolArk"
      : "Hoymiles";

  const hasWeatherStrip = device.weather_condition || device.temperature || device.humidity;

  // Peer delta color class
  const getDeltaClass = (delta) => {
    if (delta == null) return "";
    if (delta >= -5) return styles.textSuccess;
    if (delta >= -15) return styles.textWarning;
    return styles.textDanger;
  };

  // SPI bar color class
  const getSpiClass = (spi) => {
    if (spi == null) return "";
    if (spi >= 95) return styles.spiGreen;
    if (spi >= 85) return styles.spiYellow;
    return styles.spiRed;
  };

  return (
    <div className={`${styles.deviceTile} ${device.is_error ? styles.deviceTileError : ""}`}>
      {/* Header */}
      <div className={`${styles.tileHeader} ${headerClass}`}>
        <div className={styles.tileHeaderLeft}>
          {device.is_error && <span className={styles.errorDot} title="Missing data"></span>}
          <span>
            {device.device_type === "enphase" ? "⊞" : device.device_type === "solArk" ? "☀" : "⚡"}
          </span>
          <span className={styles.tileDeviceName}>{deviceName}</span>
        </div>
        <div className={styles.tileHeaderRight}>
          {device.is_error && (
            <span className={styles.tileErrorBadge}>⚠ Error</span>
          )}
          <span className={styles.tileTypeBadge}>{typeLabel}</span>
        </div>
      </div>

      {/* Body */}
      <div className={styles.tileBody}>
        {/* Left Info */}
        <div className={styles.tileInfo}>
          {hasWeatherStrip && (
            <div className={styles.weatherStrip}>
              {device.weather_icon && (
                <img
                  src={device.weather_icon.startsWith("http") ? device.weather_icon : `https:${device.weather_icon}`}
                  alt=""
                  className={styles.weatherStripIcon}
                />
              )}
              <span className={styles.weatherStripText}>
                {device.weather_condition || ""}{" "}
                {device.temperature != null ? `${Math.round(device.temperature)}°F` : ""}
              </span>
              {device.humidity != null && (
                <span className={styles.weatherStripHumidity}>💧 {device.humidity}%</span>
              )}
            </div>
          )}

          <div className={styles.infoRows}>
            {device.state && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>📍 State</span>
                <span className={styles.infoValue}>{device.state}</span>
              </div>
            )}
            {!device.state && device.country && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>🌐 Country</span>
                <span className={styles.infoValue}>{device.country}</span>
              </div>
            )}
            {device.zipcode && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>📮 Zipcode</span>
                <span className={styles.infoValue}>{device.zipcode}</span>
              </div>
            )}
            {device.utility_name && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>⚡ Utility</span>
                <span className={styles.infoValue} title={device.utility_name}>
                  {device.utility_name.length > 20 ? device.utility_name.substring(0, 20) + "…" : device.utility_name}
                </span>
              </div>
            )}
            {device.wind_speed != null && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>💨 Wind</span>
                <span className={styles.infoValue}>{Math.round(device.wind_speed)} mph</span>
              </div>
            )}
            {device.cloud != null && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>☁ Cloud</span>
                <span className={styles.infoValue}>{device.cloud}%</span>
              </div>
            )}
            {device.sunrise && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>🌅 Sunrise</span>
                <span className={styles.infoValue}>{device.sunrise}</span>
              </div>
            )}
            {device.sunset && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>🌙 Sunset</span>
                <span className={styles.infoValue}>{device.sunset}</span>
              </div>
            )}
            {(device.device_type === "enphase" || device.device_type === "solArk") && device.battery_state && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>🔋 Status</span>
                <span className={styles.infoValue}>
                  <StatusBadge state={device.battery_state} />
                </span>
              </div>
            )}
          </div>

          {/* Peer Performance Section */}
          <div className={styles.tilePeerSection}>
            <div className={styles.tilePeerHeader}>
              <span>👥 Peer Comparison</span>
              <PeerStatusBadge status={device.peer_status} />
            </div>
            {device.yield_kwh_per_kw != null ? (
              <>
                <div className={styles.tilePeerMetrics}>
                  <div className={styles.peerMetric}>
                    <span className={styles.peerMetricLabel}>Yield</span>
                    <span className={styles.peerMetricValue}>
                      {device.yield_kwh_per_kw?.toFixed(2)} <small>kWh/kW</small>
                    </span>
                  </div>
                  <div className={styles.peerMetric}>
                    <span className={styles.peerMetricLabel}>Peer Median</span>
                    <span className={styles.peerMetricValue}>
                      {device.peer_median_yield != null ? device.peer_median_yield.toFixed(2) : "--"} <small>kWh/kW</small>
                    </span>
                  </div>
                  <div className={styles.peerMetric}>
                    <span className={styles.peerMetricLabel}>Delta</span>
                    <span className={`${styles.peerMetricValue} ${getDeltaClass(device.peer_delta_pct)}`}>
                      {device.peer_delta_pct != null
                        ? `${device.peer_delta_pct > 0 ? "+" : ""}${device.peer_delta_pct.toFixed(1)}%`
                        : "--"}
                    </span>
                  </div>
                  <div className={styles.peerMetric}>
                    <span className={styles.peerMetricLabel}>Percentile</span>
                    <span className={styles.peerMetricValue}>
                      {device.peer_percentile != null ? `P${device.peer_percentile}` : "--"}
                    </span>
                  </div>
                </div>
                {device.spi != null && (
                  <div className={styles.peerSpiBar}>
                    <div className={styles.peerSpiLabel}>SPI: {Math.round(device.spi)}</div>
                    <div className={styles.peerSpiTrack}>
                      <div
                        className={`${styles.peerSpiFill} ${getSpiClass(device.spi)}`}
                        style={{ width: `${Math.min(device.spi, 120) > 120 ? 100 : Math.round(device.spi)}%` }}
                      />
                    </div>
                  </div>
                )}
                {device.cohort_size > 0 && (
                  <div className={styles.peerCohortInfo}>
                    📊 {device.cohort_size} peers in cohort
                    {device.cohort_key_str ? ` · ${device.cohort_key_str}` : ""}
                  </div>
                )}
              </>
            ) : (
              <div className={styles.peerNoData}>
                ℹ No yield data — missing system size or production
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.tileFooter}>
            {device.phone_number && <span>📞 {device.phone_number}</span>}
            {device.link_to_portal && (
              <a href={device.link_to_portal} target="_blank" rel="noopener noreferrer" className={styles.tilePortalLink}>
                🔗 Portal
              </a>
            )}
            {device.full_address && (
              <span className={styles.tileAddress} title={device.full_address}>
                🏠 {device.full_address.length > 30 ? device.full_address.substring(0, 30) + "…" : device.full_address}
              </span>
            )}
          </div>
        </div>

        {/* Right Chart */}
        <div className={styles.tileChart}>
          <DeviceChart device={device} />
        </div>
      </div>
    </div>
  );
};

// =================== Main Dashboard Component ===================
const WeatherDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  // Filter state
  const [selectedDeviceType, setSelectedDeviceType] = useState("all");
  const [selectedState, setSelectedState] = useState("");
  const [selectedUtility, setSelectedUtility] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPeerDays, setSelectedPeerDays] = useState(1);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  // Map state
  const [mapVisible, setMapVisible] = useState(true);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  // ---- Fetch data ----
  const fetchDashboard = useCallback(async (params = {}) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = new URL(WEATHER_DASHBOARD_API);
      if (params.device_type && params.device_type !== "all") url.searchParams.set("device_type", params.device_type);
      if (params.state) url.searchParams.set("state", params.state);
      if (params.utility) url.searchParams.set("utility", params.utility);
      if (params.date) url.searchParams.set("date", params.date);
      if (params.peer_days) url.searchParams.set("peer_days", params.peer_days);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          navigate("/");
          return;
        }
        throw new Error("Failed to fetch weather dashboard data");
      }

      const data = await response.json();
      setDashboardData(data);

      // Set filter defaults from API response
      if (data.filters) {
        setSelectedDeviceType(data.filters.selected_device_type || "all");
        setSelectedState(data.filters.selected_state || "");
        setSelectedUtility(data.filters.selected_utility || "");
        if (data.filters.selected_date) setSelectedDate(data.filters.selected_date);
        if (data.filters.selected_peer_days) setSelectedPeerDays(data.filters.selected_peer_days);
      }
    } catch (err) {
      console.error("Weather dashboard fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ---- Google Maps ----
  useEffect(() => {
    if (!dashboardData || !dashboardData.devices || !mapRef.current) return;
    if (!window.google || !window.google.maps) {
      // Load Google Maps script dynamically
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}&v=3.exp&libraries=geometry`;
        script.async = true;
        script.defer = true;
        script.onload = () => initMap();
        document.head.appendChild(script);
      } else {
        // Wait for existing script to load
        const checkGoogle = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkGoogle);
            initMap();
          }
        }, 100);
      }
    } else {
      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  const initMap = () => {
    if (!mapRef.current || !window.google) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 5,
      center: { lat: 39.8283, lng: -98.5795 },
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        position: window.google.maps.ControlPosition.TOP_RIGHT,
      },
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
    mapInstanceRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow();

    const bounds = new window.google.maps.LatLngBounds();
    let hasMarkers = false;
    const geocodeQueue = [];

    (dashboardData.devices || []).forEach((device) => {
      if (device.lat && device.lon) {
        const pos = { lat: device.lat, lng: device.lon };
        placeMarker(device, pos, map);
        bounds.extend(pos);
        hasMarkers = true;
      } else if (device.zipcode || device.full_address) {
        geocodeQueue.push(device);
      }
    });

    // Geocode remaining
    const geocoder = new window.google.maps.Geocoder();
    let delay = 0;
    geocodeQueue.forEach((device) => {
      setTimeout(() => {
        const query = device.full_address || device.zipcode;
        geocoder.geocode({ address: query }, (results, status) => {
          if (status === "OK" && results[0]) {
            const pos = results[0].geometry.location;
            placeMarker(device, pos, map);
            bounds.extend(pos);
            if (markersRef.current.length > 1) {
              map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
            }
          }
        });
      }, delay);
      delay += 150;
    });

    if (hasMarkers && markersRef.current.length > 1) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
        if (map.getZoom() < 4) map.setZoom(4);
      });
    } else if (hasMarkers && markersRef.current.length === 1) {
      map.setCenter(markersRef.current[0].getPosition());
      map.setZoom(10);
    }
  };

  const createMarkerIcon = (deviceType) => {
    const color = MARKER_COLORS[deviceType] || "#666";
    let iconSvg = "";
    if (deviceType === "enphase") {
      iconSvg =
        '<rect x="11" y="9" width="10" height="10" rx="1" fill="' + color + '" opacity="0.8"/>' +
        '<line x1="11" y1="12" x2="21" y2="12" stroke="#fff" stroke-width="0.8"/>' +
        '<line x1="11" y1="16" x2="21" y2="16" stroke="#fff" stroke-width="0.8"/>' +
        '<line x1="14.5" y1="9" x2="14.5" y2="19" stroke="#fff" stroke-width="0.8"/>' +
        '<line x1="17.5" y1="9" x2="17.5" y2="19" stroke="#fff" stroke-width="0.8"/>';
    } else if (deviceType === "solArk") {
      iconSvg =
        '<circle cx="16" cy="14" r="3.5" fill="' + color + '" opacity="0.8"/>' +
        '<line x1="16" y1="8" x2="16" y2="9.5" stroke="' + color + '" stroke-width="1.5" stroke-linecap="round"/>' +
        '<line x1="16" y1="18.5" x2="16" y2="20" stroke="' + color + '" stroke-width="1.5" stroke-linecap="round"/>' +
        '<line x1="10" y1="14" x2="11.5" y2="14" stroke="' + color + '" stroke-width="1.5" stroke-linecap="round"/>' +
        '<line x1="20.5" y1="14" x2="22" y2="14" stroke="' + color + '" stroke-width="1.5" stroke-linecap="round"/>';
    } else {
      iconSvg = '<polygon points="17,8 12,15 15,15 14,20 19,13 16,13" fill="' + color + '" opacity="0.8"/>';
    }

    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">' +
      '<path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0z" fill="' + color + '" stroke="#fff" stroke-width="1.5"/>' +
      '<circle cx="16" cy="14" r="7" fill="#fff" opacity="0.9"/>' +
      iconSvg +
      "</svg>";

    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(32, 40),
      anchor: new window.google.maps.Point(16, 40),
    };
  };

  const placeMarker = (device, position, map) => {
    const marker = new window.google.maps.Marker({
      position,
      map,
      icon: createMarkerIcon(device.device_type),
      title: device.name || `Device #${device.device_id}`,
    });

    const typeLabel = device.device_type === "enphase" ? "Enphase" : device.device_type === "solArk" ? "SolArk" : "Hoymiles";
    const typeColor = MARKER_COLORS[device.device_type] || "#666";

    let content =
      `<div style="font-family:Arial,sans-serif;min-width:200px;max-width:300px;">` +
      `<div style="background:${typeColor};color:#fff;padding:6px 10px;border-radius:4px 4px 0 0;font-weight:600;font-size:13px;">` +
      `<span>${device.name || "Device #" + device.device_id}</span>` +
      `<span style="float:right;font-size:11px;opacity:0.9;">${typeLabel}</span></div>` +
      `<div style="padding:8px 10px;font-size:12px;line-height:1.6;">`;

    if (device.full_address) content += `<div>🏠 ${device.full_address}</div>`;
    if (device.state) content += `<div>📍 ${device.state}</div>`;
    if (device.zipcode) content += `<div>📮 ${device.zipcode}</div>`;
    if (device.temperature != null) {
      content += `<div>🌡 ${device.temperature}°F`;
      if (device.weather_condition) content += ` - ${device.weather_condition}`;
      content += `</div>`;
    }
    if (device.utility_name) content += `<div>⚡ ${device.utility_name}</div>`;
    content += `</div></div>`;

    marker.addListener("click", () => {
      infoWindowRef.current.setContent(content);
      infoWindowRef.current.open(map, marker);
    });

    markersRef.current.push(marker);
  };

  // ---- Filter Handlers ----
  const handleChangePeerDays = (days) => {
    setSelectedPeerDays(days);
    fetchDashboard({
      device_type: selectedDeviceType,
      state: selectedState,
      utility: selectedUtility,
      date: selectedDate,
      peer_days: days,
    });
  };

  const handleApplyFilters = () => {
    fetchDashboard({
      device_type: selectedDeviceType,
      state: selectedState,
      utility: selectedUtility,
      date: selectedDate,
      peer_days: selectedPeerDays,
    });
  };

  const handleResetFilters = () => {
    setSelectedDeviceType("all");
    setSelectedState("");
    setSelectedUtility("");
    setSelectedStatus("all");
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);
    fetchDashboard({ date: today });
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    navigate("/");
  };

  // ---- Computed data ----
  const stats = dashboardData?.stats || {};
  const peerPerformance = dashboardData?.peer_performance || {};

  // Filter devices by status (is_error)
  const devices = React.useMemo(() => {
    const allDevices = dashboardData?.devices || [];
    if (selectedStatus === "all") return allDevices;
    if (selectedStatus === "error") return allDevices.filter(d => d.is_error === true);
    if (selectedStatus === "normal") return allDevices.filter(d => d.is_error === false);
    return allDevices;
  }, [dashboardData?.devices, selectedStatus]);
  const filters = dashboardData?.filters || {};
  const usStates = filters.us_states || [];
  const availableUtilities = filters.available_utilities || [];

  // Top 5 locations: group devices by zipcode, pick top 5 with most devices
  const topLocations = React.useMemo(() => {
    if (!devices.length) return [];
    const locMap = {};
    devices.forEach((d) => {
      if (!d.zipcode) return;
      if (!locMap[d.zipcode]) {
        locMap[d.zipcode] = {
          zipcode: d.zipcode,
          state: d.state || "",
          temperature: d.temperature,
          weather_condition: d.weather_condition,
          weather_icon: d.weather_icon,
          feelslike: d.feelslike,
          humidity: d.humidity,
          wind_mph: d.wind_speed,
          wind_speed: d.wind_speed,
          cloud: d.cloud,
          sunrise: d.sunrise,
          sunset: d.sunset,
          device_count: 0,
        };
      }
      locMap[d.zipcode].device_count++;
      // Update weather if this device has data but existing doesn't
      if (!locMap[d.zipcode].temperature && d.temperature) {
        locMap[d.zipcode].temperature = d.temperature;
        locMap[d.zipcode].weather_condition = d.weather_condition;
        locMap[d.zipcode].weather_icon = d.weather_icon;
        locMap[d.zipcode].feelslike = d.feelslike;
        locMap[d.zipcode].humidity = d.humidity;
        locMap[d.zipcode].wind_mph = d.wind_speed;
        locMap[d.zipcode].cloud = d.cloud;
        locMap[d.zipcode].sunrise = d.sunrise;
        locMap[d.zipcode].sunset = d.sunset;
      }
    });
    return Object.values(locMap)
      .sort((a, b) => b.device_count - a.device_count)
      .slice(0, 5);
  }, [devices]);

  // State options for react-select
  const stateOptions = usStates.map(([code, name]) => ({ value: code, label: name }));
  const utilityOptions = availableUtilities.map((u) => ({ value: u, label: u }));

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: 36, fontSize: 13 }),
    menu: (base) => ({ ...base, zIndex: 999, fontSize: 13 }),
    placeholder: (base) => ({ ...base, fontSize: 13, color: "#999" }),
  };

  // ---- Render ----
  return (
    <div className={styles.dashboardContainer}>
      {loading && <Loader />}

      {/* Topbar */}
      <section className={styles.topbar}>
        <img className={styles.topbar_logo} src={logoSvg} alt="Logo" />
        <div className={styles.topbar_right}>
          <button className={styles.backBtn} onClick={() => navigate("/Home")}>
            ← Home
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </section>

      {/* Page Content */}
      <div className={styles.pageContent}>
        {/* Title */}
        <h1 className={styles.pageTitle}>
          <span className={styles.pageTitleIcon}>⛅</span>
          Solar Dashboard
        </h1>

        {error && (
          <div style={{ background: "#f8d7da", color: "#721c24", padding: "12px 16px", borderRadius: 8, marginBottom: 16 }}>
            Error: {error}
          </div>
        )}

        {/* Stats Row */}
        <div className={styles.statsRow}>
          <div className={`${styles.statCard} ${styles.statCardOrange}`}>
            <div>
              <div className={styles.statNumber}>{stats.total_count || 0}</div>
              <div className={styles.statLabel}>Total Devices</div>
            </div>
            <div className={styles.statIcon}>🖥</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardGreen}`}>
            <div>
              <div className={styles.statNumber}>{stats.enphase_count || 0}</div>
              <div className={styles.statLabel}>Enphase</div>
            </div>
            <div className={styles.statIcon}>⊞</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardYellow}`}>
            <div>
              <div className={styles.statNumber}>{stats.solar_count || 0}</div>
              <div className={styles.statLabel}>Solar (SolArk)</div>
            </div>
            <div className={styles.statIcon}>☀</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardTeal}`}>
            <div>
              <div className={styles.statNumber}>{stats.hoymiles_count || 0}</div>
              <div className={styles.statLabel}>Hoymiles</div>
            </div>
            <div className={styles.statIcon}>⚡</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardRed}`}>
            <div>
              <div className={styles.statNumber}>{stats.error_count || 0}</div>
              <div className={styles.statLabel}>Error</div>
            </div>
            <div className={styles.statIcon}>⚠</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCardBlue}`}>
            <div>
              <div className={styles.statNumber}>{stats.normal_count || 0}</div>
              <div className={styles.statLabel}>Normal</div>
            </div>
            <div className={styles.statIcon}>✓ </div>
          </div>
        </div>

        {/* Solar Peer Performance Summary */}
        <PeerPerformanceSummary
          peerPerformance={peerPerformance}
          totalCount={stats.total_count || 0}
          selectedPeerDays={selectedPeerDays}
          onChangePeerDays={handleChangePeerDays}
        />

        {/* Filters */}
        <div className={styles.filtersCard}>
          <div className={styles.filtersHeader}>🔍 Filters</div>
          <div className={styles.filtersBody}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Device Type</label>
              <select
                className={styles.filterSelect}
                value={selectedDeviceType}
                onChange={(e) => setSelectedDeviceType(e.target.value)}
              >
                <option value="all">All Device Types</option>
                <option value="enphase">Enphase</option>
                <option value="solArk">Solar (SolArk)</option>
                <option value="hoymiles">Hoymiles</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>State</label>
              <Select
                className={styles.reactSelectContainer}
                styles={selectStyles}
                options={stateOptions}
                value={stateOptions.find((o) => o.value === selectedState) || null}
                onChange={(opt) => setSelectedState(opt ? opt.value : "")}
                isClearable
                placeholder="Search or select a state..."
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Utility</label>
              <Select
                className={styles.reactSelectContainer}
                styles={selectStyles}
                options={utilityOptions}
                value={utilityOptions.find((o) => o.value === selectedUtility) || null}
                onChange={(opt) => setSelectedUtility(opt ? opt.value : "")}
                isClearable
                placeholder="Search or select a utility..."
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status</label>
              <select
                className={styles.filterSelect}
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="error">Error</option>
                <option value="normal">Normal</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>📅 Date</label>
              <input
                type="date"
                className={styles.filterInput}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className={styles.filterActions}>
              <button className={styles.btnApply} onClick={handleApplyFilters}>
                🔍 Apply
              </button>
              <button className={styles.btnReset} onClick={handleResetFilters}>
                ↩ Reset
              </button>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className={styles.mapCard}>
          <div className={styles.mapHeader}>
            <div className={styles.mapTitle}>🗺 Device Map</div>
            <div className={styles.mapLegend}>
              <span className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: "#28a745" }}></span> Enphase
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: "#e0a800" }}></span> SolArk
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: "#17a2b8" }}></span> Hoymiles
              </span>
              <button
                className={styles.toggleMapBtn}
                onClick={() => {
                  setMapVisible((v) => !v);
                  if (!mapVisible && mapInstanceRef.current) {
                    setTimeout(() => {
                      window.google.maps.event.trigger(mapInstanceRef.current, "resize");
                    }, 100);
                  }
                }}
              >
                {mapVisible ? "▲" : "▼"}
              </button>
            </div>
          </div>
          <div
            ref={mapRef}
            className={mapVisible ? styles.mapBody : styles.mapBodyHidden}
            style={!mapVisible ? { height: 0, overflow: "hidden" } : { height: 300 }}
          ></div>
        </div>

        {/* Weather Overview - Top 5 Locations */}
        <div className={styles.weatherOverviewCard}>
          <div className={styles.weatherOverviewHeader}>⛅ Weather Overview — Top 5 Locations</div>
          <div className={styles.weatherOverviewBody}>
            <div className={styles.locationsRow}>
              {topLocations.length > 0 ? (
                topLocations.map((loc, idx) => <LocationWeatherCard key={idx} loc={loc} />)
              ) : (
                <div className={styles.noWeatherData}>
                  <div>☁</div>
                  <div>No weather data available</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Device Cards Grid */}
        {devices.length > 0 ? (
          <div className={styles.devicesGrid}>
            {devices.map((device, idx) => (
              <DeviceTile key={device.device_id || idx} device={device} />
            ))}
          </div>
        ) : (
          !loading && (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>📥</div>
              <div className={styles.emptyStateTitle}>No Devices Found</div>
              <div className={styles.emptyStateText}>Try adjusting your filters or check back later.</div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default WeatherDashboard;
